import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Info, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useSuccessToast, useWarningToast } from '@/components/ErrorToast';
import { useAuthContext } from '@/hooks/useAuth';
import { isSetupPendingError } from '@/services/auth/shared/errorHandling';
import env from '@/config/env';
import { TokenStorage } from '@/services/auth';
import { unwrapEnvelope } from '@/utils/api-envelope';

/**
 * NotificationModalShadcn v3.0 - Full Feature Implementation
 *
 * @description
 * Material-UI Design System v2.0準拠のshadcn/ui Dialog実装
 * - Backend API統合（http://localhost:3000/api/notifications）
 * - リアルタイム通知取得・既読管理
 * - カテゴリー別ページ遷移
 * - 楽観的UI更新
 * - エラーハンドリング
 * - 既存UI/UX完全維持
 */

/**
 * 通知タイプ（UIレベル）
 */
type NotificationType = 'warning' | 'info' | 'error';

/**
 * 通知カテゴリー（Backend準拠）
 */
type NotificationCategory =
  | 'all'
  | 'reservation-new'
  | 'reservation-cancelled'
  | 'reservation-changed'
  | 'reservation-reminder'
  | 'customer-new'
  | 'customer-birthday'
  | 'customer-feedback'
  | 'customer-visit-milestone'
  | 'payment-success'
  | 'payment-failed'
  | 'daily-sales-summary'
  | 'refund-processed'
  | 'staff-shift-reminder'
  | 'staff-break-time'
  | 'staff-performance'
  | 'inventory-low'
  | 'maintenance-required'
  | 'system-alert';

/**
 * 期間フィルター
 */
type PeriodFilter = 'today' | 'week' | 'month' | 'all';

/**
 * 通知データ構造（Backend準拠）
 */
interface Notification {
  id: string;
  tenantId: string;
  userId?: string;
  category: NotificationCategory;
  title: string;
  message: string;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  metadata?: Record<string, unknown>;
  actionType?: string;
  actionUrl?: string;
  isRead: boolean;
  readAt?: string;
  isArchived: boolean;
  archivedAt?: string;
  createdAt: string;
  expiresAt?: string;
}

/**
 * Props
 */
export interface NotificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Backend API URL
 */
const API_BASE_URL = env.getApiConfig().baseUrl;

/**
 * カテゴリー→通知タイプマッピング（UI表示用）
 */
const getCategoryType = (category: NotificationCategory): NotificationType => {
  if (category === 'all') return 'info';

  // 警告系（オレンジ）
  if (
    category.includes('cancelled') ||
    category.includes('failed') ||
    category.includes('low') ||
    category.includes('reminder')
  ) {
    return 'warning';
  }

  // エラー系（赤）
  if (category.includes('alert') || category.includes('maintenance')) {
    return 'error';
  }

  // デフォルトは情報系（青）
  return 'info';
};

/**
 * カテゴリー→時刻表示変換
 */
const formatTimeAgo = (createdAt: string): string => {
  const now = new Date();
  const created = new Date(createdAt);
  const diffMs = now.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  return `${diffDays}日前`;
};

/**
 * 通知タイプに応じたスタイルとアイコンを返す
 */
const getNotificationStyle = (type: NotificationType) => {
  switch (type) {
    case 'warning':
      return {
        borderColor: 'border-l-orange-500',
        bgColor: 'bg-orange-50/10',
        iconColor: 'text-orange-600',
        Icon: AlertTriangle,
      };
    case 'info':
      return {
        borderColor: 'border-l-blue-500',
        bgColor: 'bg-blue-50/10',
        iconColor: 'text-blue-600',
        Icon: Info,
      };
    case 'error':
      return {
        borderColor: 'border-l-red-500',
        bgColor: 'bg-red-50/10',
        iconColor: 'text-red-600',
        Icon: XCircle,
      };
  }
};

/**
 * カテゴリーラベル（日本語）
 */
const categoryLabels: Record<string, string> = {
  all: '全て',
  reservation: '予約',
  customer: '顧客',
  system: 'システム',
  staff: 'スタッフ',
};

/**
 * カテゴリー→ページ遷移先マッピング
 */
const getCategoryRoute = (
  category: NotificationCategory,
  metadata?: Record<string, unknown>
): string => {
  // reservation系
  if (category.startsWith('reservation')) {
    return metadata?.reservationId
      ? `/reservations/${metadata.reservationId}`
      : '/reservations';
  }

  // customer系
  if (category.startsWith('customer')) {
    return metadata?.customerId
      ? `/customers/${metadata.customerId}`
      : '/customers';
  }

  // staff系
  if (category.startsWith('staff')) {
    return metadata?.staffId ? `/staff/${metadata.staffId}` : '/staff';
  }

  // payment/sales系
  if (category.startsWith('payment') || category.startsWith('daily-sales')) {
    return '/sales';
  }

  // system系
  if (category.startsWith('system') || category.startsWith('maintenance')) {
    return '/settings';
  }

  return '/dashboard';
};

/**
 * NotificationModal コンポーネント
 */
export const NotificationModal: React.FC<NotificationModalProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const showSuccess = useSuccessToast();
  const showWarning = useWarningToast();
  const { user } = useAuthContext();

  const [periodFilter, setPeriodFilter] = React.useState<PeriodFilter>('all');
  const [categoryFilter, setCategoryFilter] = React.useState<string>('all');
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [loading, setLoading] = React.useState(false);

  // AuthContextから組織ID・ユーザーIDを取得
  const tenantId = user?.tenantId || ''; // 動的に取得
  const userId = user?.id || 'user-test-1';
  const token = TokenStorage.getAccessToken();

  /**
   * 401レスポンスの共通ハンドリング
   * setup-pending時はトースト表示、それ以外はログインリダイレクト
   * @returns true if handled (caller should return early)
   */
  const handleAuthError = React.useCallback(
    async (response: Response): Promise<boolean> => {
      if (response.status !== 401) return false;
      const errorData = await response
        .clone()
        .json()
        .catch((e: unknown) => {
          console.warn('[NotificationModal] Failed to parse 401 response:', e);
          return {};
        });
      if (isSetupPendingError(errorData)) {
        toast.info('アカウントを準備中です。しばらくお待ちください...', {
          id: 'setup-pending',
        });
        return true;
      }
      showWarning('認証が必要です。再ログインしてください');
      navigate('/login');
      return true;
    },
    [showWarning, navigate]
  );

  /**
   * 通知一覧を取得
   */
  const fetchNotifications = React.useCallback(async () => {
    // トークンがない場合は事前チェック
    if (!token) {
      showWarning('認証情報が見つかりません。再ログインしてください');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        tenantId,
        userId,
        limit: '50',
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      const response = await fetch(`${API_BASE_URL}/notifications?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (await handleAuthError(response)) return;

      if (response.status === 403) {
        showWarning('この操作の権限がありません');
        return;
      }

      if (!response.ok) {
        throw new Error('通知の取得に失敗しました');
      }

      const raw = await response.json();
      const data = unwrapEnvelope<{ notifications?: Notification[] }>(raw);
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('通知取得エラー:', error);
      showWarning('通知の取得に失敗しました');
      setNotifications([]); // エラー時は空配列に設定
    } finally {
      setLoading(false);
    }
  }, [tenantId, userId, token, showWarning, navigate, handleAuthError]);

  /**
   * モーダルが開いたときに通知を取得
   */
  React.useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  /**
   * フィルター適用後の通知リスト
   */
  const filteredNotifications = React.useMemo(() => {
    return notifications.filter((notification) => {
      // カテゴリーフィルター
      if (categoryFilter !== 'all') {
        // 簡易マッピング: reservation-*はreservation、customer-*はcustomerとマッチ
        if (
          !notification.category.startsWith(categoryFilter.replace(/s$/, ''))
        ) {
          return false;
        }
      }

      // 期間フィルター
      const timeAgo = formatTimeAgo(notification.createdAt);
      if (periodFilter === 'today') {
        return timeAgo.includes('分前');
      } else if (periodFilter === 'week') {
        return (
          timeAgo.includes('時間前') ||
          timeAgo.includes('分前') ||
          timeAgo.includes('日前')
        );
      }

      return true;
    });
  }, [notifications, periodFilter, categoryFilter]);

  /**
   * 未読通知数
   */
  const unreadCount = filteredNotifications.filter((n) => !n.isRead).length;

  /**
   * 全て既読にする処理
   */
  const handleMarkAllAsRead = async () => {
    // トークンがない場合は事前チェック
    if (!token) {
      showWarning('認証情報が見つかりません。再ログインしてください');
      navigate('/login');
      return;
    }

    try {
      const params = new URLSearchParams({
        tenantId,
        userId,
      });

      const response = await fetch(
        `${API_BASE_URL}/notifications/mark-all-read?${params}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (await handleAuthError(response)) return;

      if (response.status === 403) {
        showWarning('この操作の権限がありません');
        return;
      }

      if (!response.ok) {
        throw new Error('既読処理に失敗しました');
      }

      // API成功後にUI更新
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          isRead: true,
          readAt: new Date().toISOString(),
        }))
      );

      showSuccess('全ての通知を既読にしました');
    } catch (error) {
      console.error('既読処理エラー:', error);
      showWarning('既読処理に失敗しました');
    }
  };

  /**
   * 通知カードクリック処理
   */
  const handleNotificationClick = async (notification: Notification) => {
    // トークンがない場合は事前チェック
    if (!token) {
      showWarning('認証情報が見つかりません。再ログインしてください');
      navigate('/login');
      return;
    }

    try {
      // 既読マーク
      if (!notification.isRead) {
        const response = await fetch(
          `${API_BASE_URL}/notifications/${notification.id}/read`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (await handleAuthError(response)) return;

        if (response.status === 403) {
          showWarning('この操作の権限がありません');
          return;
        }

        if (response.ok) {
          // API成功後にUI更新
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === notification.id
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            )
          );
        }
      }

      // ページ遷移
      const route = getCategoryRoute(
        notification.category,
        notification.metadata
      );
      onOpenChange(false); // モーダルを閉じる
      navigate(route);
    } catch (error) {
      console.error('通知処理エラー:', error);
      showWarning('通知の処理に失敗しました');
    }
  };

  /**
   * アクションボタンクリック処理
   */
  const handleActionClick = async (
    e: React.MouseEvent,
    notification: Notification
  ) => {
    e.stopPropagation();

    // トークンがない場合は事前チェック
    if (!token) {
      showWarning('認証情報が見つかりません。再ログインしてください');
      navigate('/login');
      return;
    }

    try {
      // 既読マーク
      if (!notification.isRead) {
        const response = await fetch(
          `${API_BASE_URL}/notifications/${notification.id}/read`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (await handleAuthError(response)) return;

        if (response.status === 403) {
          showWarning('この操作の権限がありません');
          return;
        }

        if (response.ok) {
          // 楽観的UI更新
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === notification.id
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            )
          );
        }
      }

      // ページ遷移
      const route =
        notification.actionUrl ||
        getCategoryRoute(notification.category, notification.metadata);
      onOpenChange(false);
      navigate(route);
    } catch (error) {
      console.error('アクション処理エラー:', error);
      showWarning('アクションの処理に失敗しました');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col gap-6 p-6">
        {/* ヘッダー */}
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-2xl font-semibold">
                お知らせ
              </DialogTitle>
              {/* Issue #2906: Radix の A11y warning (Missing Description) 解消。
                  sr-only でスクリーンリーダーにはコンテキストを伝えつつ視覚には非表示。 */}
              <DialogDescription className="sr-only">
                未読の通知とお知らせを確認できます
              </DialogDescription>
              <Badge variant="secondary" className="text-sm font-medium">
                {unreadCount}件の通知
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0 || loading}
              className="text-sm font-medium hover:bg-gray-100"
            >
              全て既読
            </Button>
          </div>
        </DialogHeader>

        {/* 期間フィルター（Tabs） */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-3">期間</p>
          <Tabs
            value={periodFilter}
            onValueChange={(value) => setPeriodFilter(value as PeriodFilter)}
          >
            <TabsList className="grid w-full grid-cols-4 h-10">
              <TabsTrigger value="today" className="text-sm font-medium">
                今日
              </TabsTrigger>
              <TabsTrigger value="week" className="text-sm font-medium">
                今週
              </TabsTrigger>
              <TabsTrigger value="month" className="text-sm font-medium">
                今月
              </TabsTrigger>
              <TabsTrigger value="all" className="text-sm font-medium">
                全て
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* カテゴリーフィルター（Button） */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-3">カテゴリー</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(categoryLabels).map(([key, label]) => (
              <Button
                key={key}
                variant={categoryFilter === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoryFilter(key)}
                className="text-sm font-medium"
              >
                {label}
              </Button>
            ))}
          </div>
        </div>

        {/* 通知カードリスト */}
        <ScrollArea className="flex-1 -mr-6 pr-6">
          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-sm text-gray-500">読み込み中...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-sm text-gray-500">
                  該当する通知がありません
                </p>
              </div>
            ) : (
              filteredNotifications.map((notification) => {
                const type = getCategoryType(notification.category);
                const style = getNotificationStyle(type);
                const { Icon } = style;

                return (
                  <Card
                    key={notification.id}
                    className={`${style.borderColor} ${style.bgColor} border-l-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                      notification.isRead ? 'opacity-60' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* アイコン */}
                        <div
                          className={`${style.iconColor} flex-shrink-0 mt-0.5`}
                        >
                          <Icon className="w-6 h-6" />
                        </div>

                        {/* コンテンツ */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base leading-tight mb-2 text-gray-900">
                            {notification.title}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {formatTimeAgo(notification.createdAt)} •{' '}
                            {notification.message}
                          </p>
                        </div>

                        {/* アクションボタン */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleActionClick(e, notification)}
                          className="flex-shrink-0 bg-white hover:bg-gray-50 text-sm font-medium"
                        >
                          詳細を見る
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationModal;
